// Engine ↔ render CONTRACT — the typed boundary between wizard input, the two engines, and
// the passport render tree (roadmap A2 · "типизированные контракты"). The shapes are FROZEN:
// the 8 datasets under docs/design-datasets/runs/*.json are the reference, and detect() output
// is proven byte-equal to them. This module makes that boundary explicit (JSDoc typedefs) and
// checkable at runtime (validate*), so Track B (viewFromPassport per section) and Track C
// (wizard → input) build against a named contract instead of `any[]`. Global app STATE is not
// here — that belongs to the wizard (Track C/D); the passport is a pure function of a Passport.

/**
 * @typedef {Object} SlidersRow  one stage · seven L-layers, each a number in [-3, 3]
 *   (float — CD removed the slider snap, s443.d5), or `null` when the layer is untouched.
 *   TWO accepted forms (the engine normalizes both):
 *     · FLAT   `{Vitality, …, Lasting}`                          (datasets 01/04/05/06/07/08)
 *     · RICH   `{ values: {Vitality,…}, touched: {Vitality:bool,…} }` (datasets 02/03)
 *   RICH is the canonical wizard form — `touched` (which layers the user actually moved) is a
 *   System-1 signal; Track B/C should standardise on it. Validation accepts either.
 * @property {number|null} [Vitality] @property {number|null} [Security] @property {number|null} [Growth]
 * @property {number|null} [Authorship] @property {number|null} [Wanting] @property {number|null} [Yourself]
 * @property {number|null} [Lasting] @property {Object} [values] @property {Object} [touched]
 *
 * @typedef {Object} TextRow  one stage · three open-field T-answers (may be '')
 *   (T4 "who was around" dropped s443.d1 / s452.d1; T3 is the §VI quote source).
 *   NB: this line said "s446.d1" until cc-31 — the lone stale trace against seven other places
 *   (quotes/thresholds.js, quotes/prompts.js, SectionVI.jsx, sectionVIBoards.js, parse-extract.mjs,
 *   DETECTION_RULES_SPEC §38). Caught by web-Claude diffing the step-3 spec; it had already been
 *   copied out of here into a dump, which is how a single stale reference starts spreading.
 * @property {string} T1 @property {string} T2 @property {string} T3
 *
 * @typedef {Object} ButtonRow  one stage · four step-6 multi-select button groups (BG1–BG4).
 *   RENDER-ONLY input: detect() ignores these. §II shows the choices directly; §IV/§VII/s7/s8
 *   build evidence pills from them ("↓ trigger" ← BG1, "→ through" ← BG2). Each group holds the
 *   user's selected option strings ("choose 1–3, can combine"); BG3 may also carry a free custom
 *   string ("+ something else"). Canonical option copy lives in BUTTON_GROUPS below (web-Claude,
 *   mockup v9.17). Parallel to sliders/text/stages by index (row i = stage i).
 * @property {string[]} BG1 @property {string[]} BG2 @property {string[]} BG3 @property {string[]} BG4
 *
 * @typedef {Object} StageMeta  wizard-authored stage metadata (names/dates/current).
 *   Shape is checked by `validateWizardInput`; the temporal invariants (one current, last,
 *   chronological, contiguous) are advisory — see `wizardInputWarnings`.
 * @property {string} name @property {number|null} startY  decimal year @property {number|null} endY
 * @property {boolean} current @property {boolean} [openStart]  open-ended start (no start date)
 *
 * @typedef {Object} WizardInput  the engine's input — same shape datasets store under `input`
 * @property {{ sliders: SlidersRow[], text?: TextRow[], stages?: StageMeta[], buttons?: ButtonRow[] }} wizard
 *
 * @typedef {Object} System1  detect() output — §II–§V/§VII geometry (deterministic, no LLM)
 * @property {any} mainPattern @property {any} redLine @property {any[]} cards @property {any} meta
 *
 * @typedef {Object} System2  selectQuotes() output — §VI/§VII quotes (needs Haiku judge)
 * @property {any[]} [perStage] @property {any} [transport] @property {any} [sectionIV]
 * @property {any} [sectionVI] @property {any} [sectionVII]
 *
 * @typedef {Object} CarrierStages  System-1 DERIVED evidence line (not a detect() output —
 *   quote-spec §13: "publishes indices OR a documented derivation"; derivation lives in
 *   `src/insights/carrierStages.js`, per detection-spec §3.5). The stages §II/§IV/§V and
 *   wizard s5/s6 mark as carrier evidence (D&R s486.d1), and the stages System 2 flags with
 *   `carrier_evidence_prompt`. Present offline too — with system2 null the line still draws,
 *   only the quotes go silent (s454). Family-level: NOT the same thing as a §V card's
 *   per-detection `evidence` (`cards.js:evidenceStages`); the two agree only by accident.
 *   `indices` is [] for scatter (no single carrier, §2.9); §IV still renders — names no single line.
 * @property {number[]} indices  ascending, deduped, in range @property {string} derivation  human-readable rule
 *
 * @typedef {Object} Passport  buildPassport() output — the single seam the sections read
 * @property {WizardInput} input @property {System1} system1 @property {CarrierStages} carrierStages
 * @property {System2|null} system2
 * @property {Array<number|null>} silhouette  mean of touched layers per stage (s443.d5 Ф1 · not
 *   ÷7). ⚠️ `null` = ЭТАП БЕЗ ЕДИНОГО ТРОНУТОГО СЛОЯ, то есть «данных нет» (s836.d3) — не ноль:
 *   ноль сфабриковал бы значение, которого человек не давал, и читался бы как точка данных.
 *   Читатель, которому нужно число, интерполирует дырку локально (`filled()` в
 *   `insights/input.js`) и заполненное назад НЕ пишет. @property {number} stageCount
 */

/**
 * Семь слоёв, в каноническом порядке. Порядок несущий, а не алфавитный: по нему идут ряды §III,
 * колонки датасетов и индексы `mainPattern.layers` — переставить их значит переставить чужие
 * данные. Имена совпадают с ключами слайдеров визарда.
 */
export const LAYERS = ['Vitality', 'Security', 'Growth', 'Authorship', 'Wanting', 'Yourself', 'Lasting'];

/**
 * The wizard's custom-answer marker. Deck v8 pushes this literal into the BG3 selection array and
 * holds the typed text in a separate per-stage store; `toWizardInput` resolves the pair (s510.d4).
 * It must never reach a Passport — see the check in validateWizardInput.
 */
export const CUSTOM_SENTINEL = '__cust';

/**
 * Stage ceiling. The wizard already enforces it (`s2 Stage Gate v5`: MAX=15) and every render
 * roman table stops at XV, but nothing between them did — an import of 16 stages validated clean
 * and then §V printed the literal "undefined" as the ember NOW tick of every mini-chart. cc-30.
 */
export const MAX_STAGES = 15;

/**
 * Slider range — float, no detents (Deck v8 clamps the drag and rounds to 2dp). s510.d1.
 *
 * ⚠️ ЭТОТ КОММЕНТАРИЙ ОТОРВАЛСЯ ОТ СВОЕГО КОДА И ДОКУМЕНТИРОВАЛ ЧУЖОЙ (аудит 21.08). Он стоял
 * вплотную над `SLIDER_MIN`, а потом между ними вклинился блок `MAX_STAGES` (cc-30) — и строка
 * про float-шкалу стала читаться как описание потолка этапов. Смысл был цел, привязка потеряна.
 * Порода та же, что «заметка пережила факт», только здесь заметка пережила своё МЕСТО.
 */
export const SLIDER_MIN = -3;
export const SLIDER_MAX = 3;

/**
 * Stage-name ceiling, carried as DATA so the wizard and the render share one number (cc-31,
 * Kirill 2026-08-20). It has always existed — but only in the render (`SectionII.CAP_NAME`), which
 * cuts on the word boundary and marks the card `data-cap-applied="name:80"`. So the limit was real
 * and invisible: a person learned about it by seeing their stage name truncated in the finished
 * passport. Step 2 must show it at typing time instead.
 *
 * Like BUTTON_GROUPS[].max (s510.d2), the validator deliberately does NOT reject on it: the cap is
 * an input-time rule, and imported biographies must keep validating. The render-side cut stays as
 * the guard for exactly those non-wizard producers.
 */
export const STAGE_NAME_MAX = 80;

/**
 * Потолок печатной строки «для чего я тогда жил» (§II, `stages[i].living_for` — значение BG3,
 * включая кастом-ответ). Поднят в контракт по решению Кирилла 21.08 — ровно тем же
 * обоснованием, что и `STAGE_NAME_MAX`: **число нужно двум сторонам, значит хранится в одном
 * месте.** Иначе при сборке шага 3 разработчику придётся скопировать 64 себе, и дальше это две
 * копии одного числа, которые однажды разойдутся.
 *
 * ПОЧЕМУ ЭТО ПОНАДОБИЛОСЬ ОТДЕЛЬНЫМ РЕШЕНИЕМ. Считалось, что 80 и 64 — разные классы: 80 будто
 * бы валидация ввода, 64 — обрезка при печати. **Замер (аудит 21.08) это опроверг:** обе
 * обрезки стоят рядом в одной функции `SectionII.Card`, обе молчаливые, а `maxlength` в деке
 * не существует вовсе — то есть 80 на вводе тоже никем не enforced. Один класс, отличавшийся
 * только местом хранения.
 *
 * Как и `STAGE_NAME_MAX` / `BUTTON_GROUPS[].max`, валидатор на нём НЕ отклоняет: это правило
 * ввода, а импортированные биографии обязаны продолжать валидироваться. Обрезка в рендере
 * остаётся гардом ровно для таких не-визардных производителей.
 *
 * Долг ввода (показать лимит при печатании, а не постфактум) — `s510.d3`, тикет
 * `3c119875cbb8813ea28bd4d035d2bc30`, ждёт экрана деки в Track C.
 */
export const LIVING_FOR_MAX = 64;

/**
 * Год чтения — якорь для «сейчас» на всех поверхностях документа.
 *
 * Куда смотрит. Последний этап открыт (`current`) и его `endY` не проставлен — по контракту это
 * легальная деградация (`toWizardInput`: `nowY ?? null`). Тогда конец дуги берётся отсюда, а не
 * считается отсутствующим. Так уже вёл себя §VII (`nowYear`/`horizon`) и §II (строка `years`);
 * §I единственный молча ронял обе формы дуги — `2004 → 2026` и `'04 → NOW` исчезали разом,
 * потому что `endY = null` давал `dated = false` (леджер, строка 27; cc-31).
 *
 * Почему в контракте. До этого число 2026 стояло в трёх местах: дефолтом опции `viewVII`,
 * литералом внутри `viewII` и нигде в §I. Тот же класс, что `LIVING_FOR_MAX` — значение, нужное
 * нескольким сторонам, обязано иметь один дом (`s524.d2`).
 *
 * Не «сегодня». Из часов не читается: документ обязан рендериться байт-в-байт одинаково завтра и
 * через год, иначе голдены парити начнут гнить по календарю. Живой год приходит с визарда
 * (`nowY`, замороженный на сабмите); это — якорь на случай, когда его не передали.
 */
export const READING_YEAR = 2026;

// NB: "step-6" here is a LEGACY wizard s-id (deep-dive). Canon numbering is sequential
// 1–7 (s449): this is now шаг 3 (deck). Contract KEYS are unchanged (web-Claude ruling);
// the label is kept only to match the frozen Field Mapping references.
/** Step-6 button-group keys, in render order. See ButtonRow / BUTTON_GROUPS. */
export const BUTTON_GROUP_KEYS = ['BG1', 'BG2', 'BG3', 'BG4'];

/**
 * Canonical step-6 button groups — prompt + selectable options per group. RENDER-ONLY:
 * detect() never reads these. Source of truth for the copy is the frozen deck,
 * `docs/design/wizard-s3/Life Map - s3 Deck v8.html` (sha 33f797d2) — mirrored here so datasets
 * and the passport render share one option source. The validator does NOT enforce membership
 * (BG3 allows a free custom string, and copy may evolve).
 *
 * `max` — the per-group selection cap, carried as DATA (s510.d2). It lived only in the deck, so
 * the contract could not answer "how many may a user pick". The validator deliberately does NOT
 * cut on it: the cap is an input-time rule the wizard enforces, while imported biographies and
 * bench fixtures must keep validating. One source, no new rejection.
 */
export const BUTTON_GROUPS = {
  BG1: {
    prompt: 'what triggered the transition?',
    max: 3, // deck hint: 'pick 1–3, can combine'
    options: ['drawn to something new', 'tired of the old', 'wanted to prove it', 'had to', "couldn't let down", 'afraid to miss it', 'something broke or left'],
  },
  BG2: {
    prompt: 'what got you through it?',
    max: 2, // deck hint: 'pick 1–2, can combine'
    options: ['by inertia', 'out of duty', 'for a goal', 'on hope', 'on fear', 'for someone', 'because of someone'],
  },
  BG3: {
    prompt: 'what were you living for then?',
    max: 1, // deck tip: 'Your real reason for all of it. One answer.'
    options: ['for myself', 'for loved ones', 'for the kids', 'for parents', 'for the work', 'for money', 'not to let anyone down', 'just pulling through', "don't know"],
    custom: true, // "+ something else" — accepts a free user string in addition to the options
  },
  BG4: {
    prompt: "what's left of this stage in you?",
    max: 2, // deck hint: 'pick 1–2, can combine'
    options: ['learned a lot', "don't regret, won't repeat", 'still in me', 'something in the body', "still don't get it", 'regret it'],
  },
};

/** @param {WizardInput} input @returns {string[]} problems (empty = valid) */
export function validateWizardInput(input) {
  const p = [];
  const w = input && input.wizard;
  if (!w) return ['input.wizard missing'];
  if (!Array.isArray(w.sliders) || w.sliders.length === 0) p.push('wizard.sliders must be a non-empty array');
  else if (w.sliders.length > MAX_STAGES) p.push(`wizard.sliders has ${w.sliders.length} stages — the ceiling is ${MAX_STAGES} (the wizard's own MAX, and where every roman table ends)`);
  (w.sliders || []).forEach((row, i) => {
    const v = row && row.values ? row.values : row; // FLAT or RICH {values,touched}
    for (const L of LAYERS) {
      // s443.d5: a slider value is a float in [-3,3], or null when the layer is untouched.
      if (v?.[L] !== null && !Number.isFinite(v?.[L])) {
        p.push(`sliders[${i}].${L} must be a finite number or null (untouched)`);
      } else if (Number.isFinite(v?.[L]) && (v[L] < SLIDER_MIN || v[L] > SLIDER_MAX)) {
        // s510.d1: the range was documented in JSDoc and enforced nowhere, so 7.5 validated
        // clean and then drew outside the viewBox in five separate charts (§I silhouette, §II
        // mini-curve, §III, §IV red line, §VII projection) — the value→y maps are linear with
        // no clamp, so the line simply vanishes off the top and nothing reports it.
        p.push(`sliders[${i}].${L} is ${v[L]} — outside the slider range [${SLIDER_MIN}, ${SLIDER_MAX}]`);
      }
    }
  });
  if (w.text !== undefined) {
    if (!Array.isArray(w.text)) p.push('wizard.text must be an array when present');
    else if (w.text.length !== w.sliders.length) p.push(`wizard.text length ${w.text.length} != sliders ${w.sliders.length}`);
  }
  if (w.stages !== undefined) {
    if (!Array.isArray(w.stages)) p.push('wizard.stages must be an array when present');
    else {
      if (w.stages.length !== w.sliders.length) p.push(`wizard.stages length ${w.stages.length} != sliders ${w.sliders.length}`);
      w.stages.forEach((row, i) => {
        if (!row || typeof row !== 'object') { p.push(`stages[${i}] must be an object`); return; }
        if (row.name !== undefined && typeof row.name !== 'string') p.push(`stages[${i}].name must be a string`);
        for (const k of ['startY', 'endY']) {
          // decimal year or null — parse-extract emits null when the bio has no dates.
          if (row[k] !== undefined && row[k] !== null && !Number.isFinite(row[k])) {
            p.push(`stages[${i}].${k} must be a decimal year or null`);
          }
        }
        for (const k of ['current', 'openStart']) {
          if (row[k] !== undefined && typeof row[k] !== 'boolean') p.push(`stages[${i}].${k} must be a boolean`);
        }
        if (Number.isFinite(row.startY) && Number.isFinite(row.endY) && row.endY < row.startY) {
          p.push(`stages[${i}] interval is inverted (endY ${row.endY} < startY ${row.startY})`);
        }
      });
    }
  }
  if (w.buttons !== undefined) {
    if (!Array.isArray(w.buttons)) p.push('wizard.buttons must be an array when present');
    else {
      if (w.buttons.length !== w.sliders.length) p.push(`wizard.buttons length ${w.buttons.length} != sliders ${w.sliders.length}`);
      w.buttons.forEach((row, i) => {
        for (const g of BUTTON_GROUP_KEYS) {
          const sel = row?.[g];
          if (sel === undefined) continue; // a group with no selection is allowed
          if (!Array.isArray(sel)) p.push(`buttons[${i}].${g} must be an array of strings`);
          else if (!sel.every((s) => typeof s === 'string')) p.push(`buttons[${i}].${g} must contain only strings`);
          // s510.d4b: `__cust` is a WIZARD-INTERNAL marker — the deck puts it in the array and
          // keeps the typed text elsewhere, and toWizardInput resolves the pair. If it survives
          // to a passport, the resolve was skipped and §II prints the sentinel to the reader.
          // Making it an error turns a silent leak into a red test.
          else if (sel.includes(CUSTOM_SENTINEL)) p.push(`buttons[${i}].${g} still carries the ${CUSTOM_SENTINEL} sentinel — the wizard must resolve it to the typed text (s510.d4)`);
        }
      });
    }
  }
  return p;
}

/**
 * Advisory checks on `wizard.stages` — TEMPORAL invariants, deliberately NOT errors (H4, s490).
 *
 * The wizard guarantees these on the producer side: it hardcodes NOW onto the top stage, so
 * exactly-one-current and a clean chronology hold by construction (Kirill + CD, 2026-08-05,
 * ticket 3b319875cbb881078d68e886f371dd6d). A producer guarantee is not a system invariant
 * though — imported biographies (bench-29 often has no `current`), a future API or a test can
 * all reach the data layer around it. Those inputs must still RENDER: §I degrades honestly to
 * `curIdx=-1`, §III drops the now-marker. So this is a separate channel from
 * `validateWizardInput` — a warning never blocks, and callers of the hard validator are
 * untouched.
 *
 * ⚠️ ЧТО ЭТОТ КАНАЛ НЕ ЗНАЧИТ — приписка 22.08, после того как ошибку сделали дважды.
 *
 * «Предупреждение, а не ошибка» описывает, что КОНТРАКТ ОБЯЗАН ПРЕДСТАВИТЬ, а не то, какие
 * состояния бывают у продукта. Из этой строки дважды вывели обратное: раз — что нехронологичный
 * ввод легальное продуктовое состояние и рендер обязан решить, что на нём печатать; два — что по
 * нему нужны борды, копи и развилки. Оба раза неверно.
 *
 * У продукта такого состояния НЕТ. Модель А (`s484.d1`): визард собирает имя + старт, конец
 * ВЫВОДИТСЯ из старта соседа сверху, у текущего этапа конец = `now`; порядок держит шаг 2
 * (`s513.d6`), равную дату ввести нельзя (`s513.d7`, решение Кирилла 22.08 — контрол не отдаёт
 * такую дату). Нахлёст и инверсия сняты `s491.d1` как невозможные. Не-визардный вход (импорт,
 * фикстуры, дев-прогоны) вынесен в ТЕХДОЛГ и в MVP не входит; для него правило другое и оно уже
 * записано — падать громко (`s515.d4` ч.1, `assertRenderable`).
 *
 * Практически: эти предупреждения — страховка слоя данных, а НЕ основание проектировать печать.
 * Прежде чем чинить «что печатать на кривом вводе», прочитай спеку шага 2: ратифицированная
 * модель ввода сильнее, чем то, что можно вывести из валидатора и этого канала.
 * Шов проверен прогоном — `src/wizard/chronology-guarantee.test.js`.
 *
 * @param {WizardInput} input @returns {string[]} warnings (empty = nothing to flag)
 */
/**
 * Пол для предупреждения о дырке между этапами. Ниже него щель не сообщается.
 *
 * ЗАМЕР, а не вкус (cc-31, 29 экстрактов, 101 соседняя пара). Дырок было 63, и из них
 * **56 размером ровно в один месяц**, медиана — месяц, больше года — ОДНА (13 мес).
 * Это не ошибки данных: биографии писали люди месяцами — «закончилось в мае, следующее
 * началось в июне», — и на границе месяцев десятичные годы расходятся на 1/12.
 *
 * ПОЧЕМУ ГЛУШИМ, А НЕ ЧИНИМ ДАННЫЕ. По модели А (`s484.d1`) визард собирает только начало
 * этапа, конец ЕСТЬ начало соседнего. Значит на живом пути щель невозможна по конструкции —
 * проверка ловила то, чего продукт не производит, и 62 шумовые строки прятали одну настоящую.
 * Пересечения порога НЕ получают: там медиана 11 месяцев, ни одного ≤ месяца, округлением
 * не объясняются — это противоречия внутри самих текстов.
 *
 * Порог намеренно грубый (год): он отделяет «границу месяцев» от «пропущенного куска жизни»,
 * а не калибрует чувствительность. Решение Кирилла 2026-08-20, развилка «в» s515.
 */
export const GAP_WARN_YEARS = 1;

export function wizardInputWarnings(input) {
  const w = input && input.wizard;
  const stages = w && w.stages;
  if (!Array.isArray(stages) || stages.length === 0) return []; // absent stages is legal
  const out = [];
  const N = stages.length;

  const current = stages.map((s, i) => (s && s.current === true ? i : -1)).filter((i) => i >= 0);
  if (current.length === 0) {
    out.push('no stage is marked current — the passport reads the last stage as "now" (present-tense copy may be stale)');
  } else if (current.length > 1) {
    out.push(`${current.length} stages marked current (indices ${current.join(', ')}) — exactly one expected`);
  } else if (current[0] !== N - 1) {
    // Contract order is oldest-first, idx N−1 = now (D&R s485.d1). Step 2 collects newest-first
    // and Track C reverses on the way in; a current in the middle means that reverse was missed.
    out.push(`current stage is index ${current[0]}, not the last (${N - 1}) — contract order is oldest-first (D&R s485.d1)`);
  }

  for (let i = 1; i < N; i++) {
    const prev = stages[i - 1];
    const cur = stages[i];
    if (Number.isFinite(prev?.startY) && Number.isFinite(cur?.startY)) {
      if (cur.startY < prev.startY) {
        out.push(`stages[${i}].startY ${cur.startY} is earlier than stages[${i - 1}].startY ${prev.startY} — stages must run oldest-first`);
      } else if (cur.startY === prev.startY) {
        // cc-31 (Kirill, 2026-08-20): equal starts mean a ZERO-LENGTH stage — the one case the
        // step-2 board calls forbidden outright, and the one the check used to miss, because it
        // stood on a strict `<`. Advisory, not an error, and deliberately so: the wizard collects
        // MM/YYYY, where two stages cannot honestly start on the same date, but an IMPORT carries
        // year granularity, and two stages starting in different months of one year collapse to
        // the same decimal year legitimately. Same reasoning as the gap/overlap split above.
        // Measured before loosening the wording: 29 extracts · 142 stages · 101 neighbour pairs ·
        // ZERO equal and ZERO out-of-order, so nothing real trips this today.
        // The load-bearing half of the fix is input-time in Track C — step 2 must not let a user
        // enter a start equal to the previous one.
        out.push(`stages[${i}].startY equals stages[${i - 1}].startY (${cur.startY}) — a stage of zero length; step 2 must not allow it (year-granularity imports can hit this legitimately)`);
      }
    }
    // Model А (D&R s484.d1): only the start is user input, a stage's end IS its neighbour's
    // start, so on the wizard path the boundary is one number. Overlap and gap are split
    // because they mean different things off that path: an overlap double-counts time and is
    // always wrong, while a gap is usually just where two months meet — see GAP_WARN_YEARS.
    // ⚠️ ЗДЕСЬ СТОЯЛО «23 of the bench-29 bios have one — whole-year ranges with a year
    // unaccounted between them». Замер это опроверг: щели есть у 23 биографий, но 56 из 63
    // размером в ОДИН МЕСЯЦ, и «год между диапазонами» встречается один раз на весь корпус.
    // Строка была догадкой о причине, записанной как наблюдение, — и она же обосновывала
    // отсутствие порога. Исправлено по замеру (cc-31).
    if (Number.isFinite(prev?.endY) && Number.isFinite(cur?.startY)) {
      if (prev.endY > cur.startY) {
        out.push(`stages[${i - 1}] ends ${prev.endY}, after stages[${i}] starts ${cur.startY} — intervals overlap`);
      } else if (cur.startY - prev.endY >= GAP_WARN_YEARS) {
        out.push(`gap of ${(cur.startY - prev.endY).toFixed(2)}y between stages[${i - 1}] (ends ${prev.endY}) and stages[${i}] (starts ${cur.startY}) — over the ${GAP_WARN_YEARS}y floor, so a year is genuinely unaccounted for (model А)`);
      }
    }
  }

  stages.forEach((s, i) => {
    if (s && s.openStart === true && i !== 0) out.push(`stages[${i}].openStart is set on a non-first stage — only the oldest stage can be open-ended`);
  });

  return out;
}

/** @param {Passport} pp @returns {string[]} problems (empty = valid) */
export function validatePassport(pp) {
  const p = [];
  if (!pp) return ['passport is null'];
  p.push(...validateWizardInput(pp.input).map((m) => 'input.' + m));
  if (!pp.system1 || typeof pp.system1 !== 'object') p.push('system1 missing');
  if (pp.system2 !== null && (typeof pp.system2 !== 'object')) p.push('system2 must be object or null');
  // ⚠️ `null` В СИЛУЭТЕ ЛЕГАЛЕН, И ДО ЭТОЙ ПРАВКИ ВАЛИДАТОР ЕГО ОТВЕРГАЛ. `s836.d3` сменил пустой
  // этап с `0` на `null` в обоих производителях (`insights/input.js:silhouette`,
  // `render/silhouette.js:stageMean`), а здесь остался `Number.isFinite`, для которого `null`
  // ложь. То есть контракт запрещал ровно то, что движок эмитит: не расхождение доки, а
  // расхождение ПРАВИЛА с кодом. Через визард ветка недостижима (пол деки — два слоя на этап,
  // `s495.d1`), но валидатор и написан для входов, которые визард не производит.
  const silOK = (v) => v === null || Number.isFinite(v);
  if (!Array.isArray(pp.silhouette) || !pp.silhouette.every(silOK)) p.push('silhouette must be an array of numbers or null (null = stage with no touched layer, s836.d3)');
  if (pp.silhouette && pp.stageCount !== pp.silhouette.length) p.push(`stageCount ${pp.stageCount} != silhouette length ${pp.silhouette?.length}`);
  return p;
}
